import React, { useEffect, useState } from 'react';
import { useImportJob } from '../../context/ImportJobContext';
import { getImportJobStatus } from '../../composables/fetchAPI';
import { usePortfolio } from '../../context/PortfolioContext';

const ImportJobTracker = () => {
  const { jobData, setJobData } = useImportJob();
  const { refreshUserPortfolios } = usePortfolio();

  const [status, setStatus] = useState<string>('QUEUED');
  const [progress, setProgress] = useState<number>(0);
  const [stats, setStats] = useState({ total: 0, processed: 0, failed: 0 });
  const [isMinimized, setIsMinimized] = useState(false); // ✅ State สำหรับย่อ-ขยาย
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // 🌟 1. ระบบจำลองแถบ Progress ให้วิ่งต่อเนื่อง
  useEffect(() => {
    // ถ้าไม่มี jobData หรือ งานเสร็จแล้ว หรือ Error แล้ว -> ไม่ต้องรัน interval
    if (!jobData || status === 'COMPLETED' || status === 'SUCCESS' || status === 'SUCCEEDED' || status === 'FAILED' || status === 'ERROR') {
      return;
    }

    // กันไม่ให้ Interval ซ้อนกัน (ถ้ากดเปิด Modal ใหม่ หรือ state เปลี่ยน)
    const interval = setInterval(() => {
      setProgress((prev) => {
        // ล็อคไว้ที่ 99% เสมอจนกว่าสถานะจะเปลี่ยน
        if (prev >= 99) return 99;

        // ถ้าเลขน้อยๆ ให้วิ่งไวหน่อย (บวกทีละ 2%)
        // ถ้าเลขเยอะแล้ว ให้วิ่งช้าลงมากๆ (บวกทีละ 0.05%)
        const increment = prev < 50 ? 2 : 0.05;

        return Math.min(prev + increment, 99);
      });
    }, 1500);

    return () => clearInterval(interval);
  }, [jobData, status]); // ✅ เพิ่ม status เข้าไปใน dependencies เพื่อให้มันหยุดเมื่อสถานะเปลี่ยน

  // 🌟 2. ระบบยิง API เช็คสถานะ (10 วิ ครั้งแรก -> 30 วิ ครั้งต่อไป)
  useEffect(() => {
    if (!jobData) return;

    let isPolling = true;
    let timeoutId: NodeJS.Timeout;

    const pollStatus = async () => {
      try {
        const res = await getImportJobStatus(jobData.portId, jobData.jobId);
        const data = res?.data;

        if (!data || !isPolling) return;

        const currentStatus = data.status || 'PROCESSING';
        setStatus(currentStatus);

        let currentTotal = 0;
        let currentProcessed = 0;
        let currentFailed = 0;

        if (data.progress && typeof data.progress === 'object') {
          currentTotal = data.progress.total || 0;
          currentProcessed = data.progress.processed || 0;
        }
        if (data.summary) {
          currentTotal = data.summary.totalItems ?? currentTotal;
          currentFailed = data.summary.failedCount ?? 0;
        }

        setStats({
          total: currentTotal,
          processed: currentProcessed,
          failed: currentFailed,
        });

        if (currentStatus === 'COMPLETED' || currentStatus === 'SUCCESS' || currentStatus === 'SUCCEEDED') {
          // รอ 2 วิ ให้ User เห็นแถบ 100% แล้วโหลดพอร์ตใหม่ ปิด Popup
          setTimeout(() => {
            if (isPolling) {
              refreshUserPortfolios();
              setJobData(null);
            }
          }, 2000);

        } else if (currentStatus === 'FAILED' || currentStatus === 'ERROR') {
          if (data.errors && data.errors.length > 0) {
            // 1. กรองเฉพาะ Error ที่มี Code ไม่ซ้ำกัน
            const uniqueErrors = Array.from(
              new Map(data.errors.map((e: any) => [e.code, e.th_message])).values()
            );

            // 2. แปลงเป็นข้อความที่จะโชว์ให้ User (เช่น โชว์ code หรือ message)
            setErrorMessage(uniqueErrors.join(' | '));
          } else {
            setErrorMessage('เกิดข้อผิดพลาดในการนำเข้าข้อมูล');
          }

        } else {
          // ถ้ายิง API มาแล้วยังไม่เสร็จ ให้ตั้งตารอรอบต่อไปที่ "30 วินาที"
          timeoutId = setTimeout(pollStatus, 30000);
        }

      } catch (error) {
        console.error("Error polling job status:", error);
        if (isPolling) {
          // ถ้ายิงพัง ให้ลองใหม่ใน 30 วิ
          timeoutId = setTimeout(pollStatus, 30000);
        }
      }
    };

    // การเริ่มทำงานครั้งแรกสุด: รอ 10 วินาที ค่อยยิง API
    timeoutId = setTimeout(pollStatus, 10000);

    return () => {
      isPolling = false;
      clearTimeout(timeoutId);
    };
  }, [jobData, setJobData, refreshUserPortfolios]);

  if (!jobData) return null;

  // --- ส่วนจัดการ UI ---
  const isError = status === 'FAILED' || status === 'ERROR' || stats.failed > 0;
  const isFinished = status === 'COMPLETED' || status === 'SUCCESS' || status === 'SUCCEEDED';
  const isSuccess = isFinished && progress >= 100; // ✅ ต้องถึง 100% ถึงจะเรียกว่าสำเร็จจริงๆ

  let statusText = 'กำลังนำเข้ารายการ...';
  if (isSuccess) {
    statusText = 'นำเข้าสำเร็จ!';
  } else if (status === 'FAILED' || status === 'ERROR') {
    statusText = 'เกิดข้อผิดพลาด';
  } else if (isFinished && progress < 100) {
    statusText = 'กำลังประมวลผลขั้นสุดท้าย...'; // กันกรณีสถานะบอกจบแต่เลขไม่ถึง
  } if (status === 'FAILED' || status === 'ERROR') statusText = 'เกิดข้อผิดพลาด';

  // แปลง progress ที่เป็นทศนิยมให้เป็นเลขจำนวนเต็มสวยๆ สำหรับแสดงผลข้อความ
  const displayPercent = Math.floor(progress);

  return (
    <div className={`fixed bottom-20 right-6 z-[999] bg-[#18181b] text-white p-5 rounded-xl shadow-2xl border border-gray-700 animate-fade-slide-up transition-all duration-300 ${isMinimized ? 'w-60 cursor-pointer' : 'w-[340px]'}`}>

      {/* ส่วนหัวที่กดเพื่อย่อ/ขยาย */}
      <div className="flex justify-between items-center mb-2 cursor-pointer" onClick={() => setIsMinimized(!isMinimized)}>
        <div>
          <h4 className="font-bold text-sm text-yellow-400">นำเข้ารายการ (CSV)</h4>
          <p className="text-[10px] text-gray-500 mt-1 uppercase tracking-wider">ID: {jobData.jobId}</p>
          <p className="text-[10px] text-gray-500 mt-1 uppercase tracking-wider">ระบบจะนำเข้าข้อมูลอัตโนมัติ</p>
        </div>
        <div className="text-gray-400 text-xs cursor-pointer hover:text-white transition-colors">
          {isError ? (
            // ❌ ปุ่มปิดเมื่อเกิด Error
            <button onClick={() => setJobData(null)} className="font-bold border border-gray-600 px-2 py-0.5 rounded hover:bg-gray-700">
              ✕ ปิด
            </button>
          ) : (
            // 🔄 ปุ่มย่อ/ขยายในสภาวะปกติ
            <div onClick={() => setIsMinimized(!isMinimized)}>
              {isMinimized ? '▲ ขยาย' : '▼ ย่อ'}
            </div>
          )}
        </div>
      </div>
      {isError && (
        <div className="text-[11px] my-2 bg-red-900/20 p-2 rounded text-red-400">
          <p className="font-bold mb-1">พบข้อผิดพลาด:</p>
          <ul className="list-disc pl-4">
            {errorMessage?.split(' | ').map((msg, idx) => (
              <li key={idx}>{msg}</li>
            ))}
          </ul>
          {/* ปุ่มแจ้ง Support */}
    <div className="mt-2 pt-2 border-t border-red-800/30">
    <button
  className="text-yellow-400 font-bold underline hover:text-yellow-300 cursor-pointer text-xs"
  onClick={(e) => {
    e.stopPropagation();
    e.preventDefault();

    // 1. เปิดหน้าต่างเปล่าๆ ไว้ก่อน "ทันที" ที่มีการคลิก
    const newWindow = window.open('', '_blank');

    // 2. สร้างข้อมูลเมล
    const email = "Support@wethaiinvest.com";
    const subject = encodeURIComponent(`แจ้งปัญหาการนำเข้าข้อมูล (Job ID: ${jobData.jobId})`);
   const body = encodeURIComponent(
  `สวัสดีทีม Support,\n\nฉันพบปัญหาในการนำเข้าไฟล์ CSV โดยมีรายละเอียดดังนี้:\n\n` +
  `Job ID: ${jobData.jobId}\n` +
  `ข้อผิดพลาดที่พบ:\n` +
  // ใช้ .map เพื่อใส่ Bullet หน้าทุกบรรทัดก่อน join
  errorMessage
    ?.replace(/[\[\]"]/g, '')
    .split(' | ')
    .map(msg => `• ${msg}`)
    .join('\n') + 
  `\n\n--------------------------------\n` +
  `โปรดระบุรายละเอียดเพิ่มเติมที่นี่...`
);

    const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=${email}&su=${subject}&body=${body}`;

    // 3. ถ้าเปิดหน้าต่างสำเร็จ ให้สั่งเปลี่ยน URL ของหน้าต่างนั้น
    if (newWindow) {
      newWindow.location.href = gmailUrl;
    } else {
      // ถ้าโดนบล็อกจริงๆ ให้แจ้งเตือน User
      alert("กรุณาอนุญาตให้เว็บไซต์เปิด Pop-up เพื่อแจ้งปัญหาทางอีเมล");
    }
  }}
>
  คลิกที่นี่เพื่อแจ้งปัญหาทางอีเมล (Gmail)
</button>
    </div>
        </div>
      )}
      {/* ถ้าไม่ถูกย่อ ให้แสดงเนื้อหาทั้งหมด */}
      {!isMinimized && (
        <>
          <div className="w-full bg-gray-800 rounded-full h-2.5 mb-3 overflow-hidden">
            <div className={`h-2.5 rounded-full transition-all duration-300 ease-out ${isError && !isSuccess ? 'bg-red-500' : isSuccess ? 'bg-green-500' : 'bg-yellow-400'}`} style={{ width: `${progress}%` }}></div>
          </div>

          <div className="flex justify-between items-center text-xs mb-3">
            <div className={`font-bold ${isError ? 'text-red-400' : isSuccess ? 'text-green-400' : 'text-gray-300'}`}>
              {/* {isSuccess
                ? 'นำเข้าสำเร็จ!'
                : isError
                  ? (errorMessage || 'เกิดข้อผิดพลาด')
                  : 'กำลังนำเข้ารายการ...'} */}
            </div>
            <span className="font-bold text-gray-200 animate-bounce">{displayPercent}%</span>
          </div>

          {stats.total > 0 && (
            <div className="grid grid-cols-3 gap-2 text-center text-[11px] pt-3 border-t border-gray-700/50">
              <div><p className="text-gray-500 mb-1">ทั้งหมด</p><p className="font-bold text-gray-300">{stats.total}</p></div>
              <div><p className="text-gray-500 mb-1">สำเร็จ</p><p className="font-bold text-green-400">{stats.processed}</p></div>
              <div><p className="text-gray-500 mb-1">ล้มเหลว</p><p className={`font-bold ${stats.failed > 0 ? 'text-red-400' : 'text-gray-400'}`}>{stats.failed}</p></div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ImportJobTracker;